/**
 * Inventory edit state (PROD-1278) — the pure half of editing the serve
 * line's inventory, shared by the Inventory page's table and the serve
 * screen's `InventoryEditDialog`.
 *
 * The worker edits four facts per row: what actually arrived (a Delivered
 * count that replaces the shipment sum until cleared, and that the production
 * record's Confirm deliveries step reads back as a prefill), how many are on
 * the shelf right now (an on-hand count that owns "left" until cleared), how
 * many the register may still sell (a for-sale count that replaces the
 * pool-derived number until cleared), and whether the item is sold out (the
 * register stops selling it). The draft is the form as typed;
 * `patchesFromDraft` diffs it against the server rows so only rows the worker
 * actually changed are sent.
 */

import type { InventoryAdjustmentPatch, InventoryRowWire } from '@/api/cafeteria-inventory'

/**
 * One row's editable state: the on-hand box as typed, the for-sale box as
 * typed, the Delivered box as typed, and the sold-out switch. `forSale` and
 * `delivered` are absent for a surface that does not edit them (the serve
 * screen's dialog edits neither): its patch then leaves the stored counts
 * alone.
 */
export interface InventoryEditDraftRow {
  onHand: string
  forSale?: string
  delivered?: string
  soldOut: boolean
}

export type InventoryEditDraft = Record<string, InventoryEditDraftRow>

/** Which optional boxes a surface shows; only the Inventory page shows the Delivered box. */
export interface InventoryDraftBoxes {
  delivered?: boolean
}

/** Seed the draft from the server rows: a stored count fills its box, otherwise the box is blank. */
export function draftFromRows(
  rows: ReadonlyArray<InventoryRowWire>,
  boxes: InventoryDraftBoxes = {},
): InventoryEditDraft {
  return Object.fromEntries(
    rows.map((row) => [
      row.itemKey,
      {
        onHand: storedCount(row.adjustment?.onHandOverride),
        forSale: storedCount(row.adjustment?.forSaleOverride),
        ...(boxes.delivered ? { delivered: storedCount(row.adjustment?.deliveredOverride) } : {}),
        soldOut: row.soldOut,
      },
    ]),
  )
}

function storedCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value)
}

/**
 * A typed count box (on hand, for sale, delivered) → the count to store. A blank box is
 * `null` (clear any stored count and fall back to the derived number);
 * `undefined` marks an unusable value (negative or not a number) that blocks
 * saving.
 */
export function parseOnHand(value: string): number | null | undefined {
  const trimmed = value.trim()
  if (trimmed === '') {
    return null
  }
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return undefined
  }
  return Math.round(parsed)
}

/**
 * The patches for rows whose on-hand count, for-sale count, Delivered count or
 * sold-out flag differs from what the server has. `undefined` = the draft
 * holds an invalid number (saving stays disabled). An empty box clears a
 * stored count. A row whose draft has no `forSale` (or `delivered`) box sends
 * no such field at all, so the stored count survives that save.
 */
export function patchesFromDraft(
  rows: ReadonlyArray<InventoryRowWire>,
  draft: InventoryEditDraft,
): InventoryAdjustmentPatch[] | undefined {
  const out: InventoryAdjustmentPatch[] = []
  for (const row of rows) {
    const edit = draft[row.itemKey]
    if (!edit) {
      continue
    }
    const onHand = parseOnHand(edit.onHand)
    if (onHand === undefined) {
      return undefined
    }
    const forSale = edit.forSale === undefined ? undefined : parseOnHand(edit.forSale)
    if (edit.forSale !== undefined && forSale === undefined) {
      return undefined
    }
    const delivered = edit.delivered === undefined ? undefined : parseOnHand(edit.delivered)
    if (edit.delivered !== undefined && delivered === undefined) {
      return undefined
    }
    const storedOnHand = row.adjustment?.onHandOverride ?? null
    const storedForSale = row.adjustment?.forSaleOverride ?? null
    const storedDelivered = row.adjustment?.deliveredOverride ?? null
    const forSaleChanged = edit.forSale !== undefined && forSale !== storedForSale
    const deliveredChanged = edit.delivered !== undefined && delivered !== storedDelivered
    if (onHand !== storedOnHand || edit.soldOut !== row.soldOut || forSaleChanged || deliveredChanged) {
      out.push({
        itemKey: row.itemKey,
        onHandOverride: onHand,
        soldOut: edit.soldOut,
        ...(edit.forSale !== undefined ? { forSaleOverride: forSale ?? null } : {}),
        ...(edit.delivered !== undefined ? { deliveredOverride: delivered ?? null } : {}),
      })
    }
  }
  return out
}
